import {useMemo, useState} from 'react';
import classNames from 'classnames';
import styles from '../styles.module.scss';
import {system} from 'services/objects';
import md5 from 'md5';
import * as inputs from './inputs';
import {initialActiveData, initialAdditionalData} from './initialData';
import {Messages} from 'services/lang/messages';
import {useAuth} from 'AuthProvider';
import {formatMessage} from 'utilsOld/formatMessage';
import {SNACKBAR_TYPES} from 'services/interfaces/global-interfaces';
import {emailValidate, loginValidate} from '../validation';

const {
  PAGES: {LOGIN, FORGOT_PASSWORD, REGISTRATION, RESTORE_PASSWORD},
  INPUT_PLACEHOLDERS: {PASSWORD, LOGIN: LOGIN_PLACEHOLDER, EMAIL, NAME},
  CONFIG_PARAMS: {REGISTRATION_ALLOWED},
  MIN_PASSWORD_LENGTH,
} = system;

const {NameField, LoginField, EmailField, PasswordField, ConfirmField} = inputs;

const HOCPageSwitcher = Component => {
  const SwitchedPage = props => {
    const {activePage, setActivePage, rightPanelActiveToggle, toggleSignInUp, activationCode, showNotification} = props;
    const {signIn, signUp, changePassword, restorePassword} = useAuth();

    /**
     * fields state
     */

    const [loginValue, setLoginValue] = useState('');
    const [passwordValue, setPasswordValue] = useState('');
    const [nameValue, setNameValue] = useState('');
    const [emailValue, setEmailValue] = useState('');

    const [confirmationValue, setConfirmationValue] = useState('');
    const [isTouched, setIsTouched] = useState({
      [LOGIN_PLACEHOLDER]: false,
      [PASSWORD]: false,
      [NAME]: false,
      [EMAIL]: false,
    });
    /**
     * validation state
     */
    const [loginError, setLoginError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [nameError, setNameError] = useState('');
    const [emailError, setEmailError] = useState('');
    /**
     * container with information
     */
    const [additionalContainer, setAdditionalContainer] = useState({...initialAdditionalData});

    /**
     * container with fields
     */
    const [activeContainer, setActiveContainer] = useState({...initialActiveData});
    /**
     * VALIDATION BLOCK
     */
    /**
     * @param {string} value
     */

    const validateLogin = value => {
      setIsTouched(prevState => ({...prevState, [LOGIN_PLACEHOLDER]: true}));
      value = value.trim();

      if (value === '') {
        setLoginError(formatMessage(Messages.Errors.FieldIsRequired, [Messages.Controls.Login]));
        return;
      }

      if (value.includes(' ') && value.length) {
        setLoginError(formatMessage(`{0} ${Messages.Errors.SpacesError}`, [Messages.Controls.Login]));
        return;
      }

      if (!loginValidate(value)) {
        setLoginError(formatMessage(`${Messages.Errors.ErrorSymbols}`, [LOGIN_PLACEHOLDER]));
        return;
      }

      setLoginError('');
    };

    const validatePassword = value => {
      setIsTouched(prevState => ({...prevState, [PASSWORD]: true}));
      value = value.trim();

      if (value === '') {
        setPasswordError(formatMessage(Messages.Errors.FieldIsRequired, [Messages.Controls.Password]));
        return;
      }

      if (value.includes(' ')) {
        setPasswordError(formatMessage(`{0} ${Messages.Errors.SpacesError}`, [Messages.Controls.Password]));
        return;
      }

      if (value.length < MIN_PASSWORD_LENGTH) {
        setPasswordError(formatMessage(Messages.Errors.SmallPassword, [String(MIN_PASSWORD_LENGTH)]));
        return;
      }

      setPasswordError('');
    };

    const validateName = value => {
      setIsTouched(prevState => ({...prevState, [NAME]: true}));
      value = value.trim();

      if (value === '') {
        setNameError(formatMessage(Messages.Errors.FieldIsRequired, [Messages.Controls.Name]));
        return;
      }

      setNameError('');
    };

    const validateEmail = value => {
      setIsTouched(prevState => ({...prevState, [EMAIL]: true}));
      value = value.trim();

      if (value === '') {
        setEmailError(formatMessage(Messages.Errors.FieldIsRequired, [Messages.Controls.Email]));
        return;
      }

      if (value.includes(' ')) {
        setEmailError(formatMessage(`{0} ${Messages.Errors.SpacesError}`, [Messages.Controls.Email]));
        return;
      }

      if (!emailValidate(value)) {
        setEmailError(formatMessage(`${Messages.Controls.Value} {0} {1}`, [EMAIL, Messages.Errors.IsIncorrect]));
        return;
      }

      setEmailError('');
    };

    const validations = {
      [LOGIN_PLACEHOLDER]: validateLogin,
      [PASSWORD]: validatePassword,
      [NAME]: validateName,
      [EMAIL]: validateEmail,
    };

    const requestValues = [
      {fieldName: LOGIN_PLACEHOLDER, fieldValue: loginValue},
      {fieldName: PASSWORD, fieldValue: passwordValue},
      {fieldName: NAME, fieldValue: nameValue},
      {fieldName: EMAIL, fieldValue: emailValue},
    ];

    const handleBlur = field => () => {
      const currentCheckedValue = requestValues.find(value => value.fieldName === field).fieldValue;
      const currentCheckedValueFunction = validations[field];

      currentCheckedValueFunction(currentCheckedValue);

      setIsTouched(prevState => ({...prevState, [field]: true}));
    };

    /**
     * here all info about active pages
     * buttons, links, titles, fields
     *
     * AdditionalContainer - container with information
     * ActiveContainer - container with form and fields
     */
    const fields = useMemo(() => {
      switch (props.activePage) {
        case REGISTRATION:
          setAdditionalContainer({
            ...additionalContainer,
            title: Messages.AdditionalText.WelcomeBack,
            text: Messages.AdditionalText.ToKeepConnected,
            buttons: [
              {
                text: Messages.Controls.SingIn.toUpperCase(),
                key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON_ADDITIONAL,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON_ADDITIONAL,
                className: classNames(styles.ghost, styles.buttonElement),
                onClick: event => {
                  setActivePage(LOGIN);
                  toggleSignInUp(event);
                },
              },
            ],
          });
          setActiveContainer({
            ...activeContainer,
            title: Messages.AdditionalText.CreateAccount,
            buttons: [
              {
                text: Messages.Controls.SingUp.toUpperCase(),
                className: classNames(styles.buttonElement, styles.buttonMarginTop),
                key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_UP_BUTTON,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_UP_BUTTON,
                onClick: () => signUpRequest(),
              },
              {
                text: Messages.Controls.SingIn.toUpperCase(),
                className: classNames(styles.buttonElement, styles.mobileButton),
                key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON,
                onClick: event => {
                  setActivePage(LOGIN);
                  toggleSignInUp(event);
                },
              },
            ],
            links: [],
          });
          return [
            <LoginField
              {...{
                key: 'LoginField',
                handleBlur,
                validate: validateLogin,
                isTouched: isTouched[LOGIN_PLACEHOLDER],
                onChange: setLoginValue,
                value: loginValue,
                errorText: loginError,
              }}
            />,
            <NameField
              {...{
                key: 'NameField',
                handleBlur,
                validate: validateName,
                isTouched: isTouched[NAME],
                onChange: setNameValue,
                value: nameValue,
                errorText: nameError,
              }}
            />,
            <EmailField
              {...{
                key: 'EmailField',
                handleBlur,
                validate: validateEmail,
                isTouched: isTouched[EMAIL],
                onChange: setEmailValue,
                value: emailValue,
                errorText: emailError,
              }}
            />,
            <PasswordField
              {...{
                key: 'PasswordField',
                handleBlur,
                validate: validatePassword,
                isTouched: isTouched[PASSWORD],
                value: passwordValue,
                errorText: passwordError,
                onChange: setPasswordValue,
              }}
            />,
          ];
        case FORGOT_PASSWORD:
          setAdditionalContainer({
            ...additionalContainer,
            title: Messages.AdditionalText.WelcomeBack,
            text: Messages.AdditionalText.ToKeepConnected,
            buttons: [
              {
                text: Messages.Controls.SingIn.toUpperCase(),
                key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON_ADDITIONAL,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON_ADDITIONAL,
                className: classNames(styles.ghost, styles.buttonElement),
                onClick: event => {
                  setActivePage(LOGIN);
                  toggleSignInUp(event);
                },
              },
            ],
          });
          setActiveContainer({
            ...activeContainer,
            title: Messages.AdditionalText.RestorePassword,
            buttons: [
              {
                text: Messages.Controls.SendRequest.toUpperCase(),
                key: system.LOGIN_PAGE_CONSTANTS.ID.SEND_BUTTON,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SEND_BUTTON,
                className: classNames(styles.buttonElement, styles.buttonMarginTop),
                onClick: () => restorePasswordRequest(),
              },
              {
                text: Messages.Controls.SingIn.toUpperCase(),
                className: classNames(styles.buttonElement, styles.mobileButton),
                key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON,
                onClick: event => {
                  setActivePage(LOGIN);
                  toggleSignInUp(event);
                },
              },
            ],
            links: [],
            classes: styles.registrationContainer,
          });
          return [
            <EmailField
              {...{
                key: 'EmailField',
                handleBlur,
                validate: validateEmail,
                isTouched: isTouched[EMAIL],
                onChange: setEmailValue,
                value: emailValue,
                errorText: emailError,
              }}
            />,
          ];
        case RESTORE_PASSWORD:
          setAdditionalContainer({
            ...additionalContainer,
            title: Messages.AdditionalText.WelcomeBack,
            text: Messages.AdditionalText.ToKeepConnected,
            buttons: [
              {
                text: Messages.Controls.SingIn.toUpperCase(),
                key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON_ADDITIONAL,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON_ADDITIONAL,
                className: classNames(styles.ghost, styles.buttonElement),
                onClick: event => {
                  setActivePage(LOGIN);
                  toggleSignInUp(event);
                },
              },
            ],
          });
          setActiveContainer({
            ...activeContainer,
            title: Messages.AdditionalText.NewPassword,
            buttons: [
              {
                text: Messages.Controls.Send,
                key: system.LOGIN_PAGE_CONSTANTS.ID.SEND_BUTTON,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SEND_BUTTON,
                className: styles.buttonElement,
                onClick: () => changePasswordRequest(),
              },
            ],
            links: [],
          });
          return [
            <PasswordField
              {...{
                key: 'PasswordField',
                handleBlur,
                validate: validatePassword,
                isTouched: isTouched[PASSWORD],
                value: passwordValue,
                errorText: passwordError,
                onChange: setPasswordValue,
              }}
            />,
            <ConfirmField
              key={'ConfirmField'}
              confirmationValue={confirmationValue}
              setConfirmationValue={setConfirmationValue}
            />,
          ];
        case LOGIN:
        default:
          /**
           * IF REGISTRATION_ALLOWED need to show Reg text and button
           * else - hide them
           */
          setAdditionalContainer({
            ...additionalContainer,
            title: Messages.AdditionalText.D5,
            text: `${REGISTRATION_ALLOWED ? Messages.AdditionalText.EnterYourDetails : ''}`,
            buttons: REGISTRATION_ALLOWED
              ? [
                  {
                    text: Messages.Controls.SingUp.toUpperCase(),
                    key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_UP_BUTTON_ADDITIONAL,
                    id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_UP_BUTTON_ADDITIONAL,
                    className: classNames(styles.ghost, styles.buttonElement),
                    onClick: event => {
                      setActivePage(REGISTRATION);
                      toggleSignInUp(event);
                    },
                  },
                ]
              : [],
          });
          setActiveContainer({
            ...activeContainer,
            title: Messages.Controls.SingIn,
            buttons: [
              {
                text: Messages.Controls.SingIn.toUpperCase(),
                key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_IN_BUTTON,
                onClick: () => signInRequest(),
                className: styles.buttonElement,
              },
              {
                text: Messages.Controls.SingUp,
                id: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_UP_BUTTON_ADDITIONAL,
                key: system.LOGIN_PAGE_CONSTANTS.ID.SIGN_UP_BUTTON_ADDITIONAL,
                className: classNames(styles.buttonElement, styles.mobileButton),
                onClick: event => {
                  setActivePage(REGISTRATION);
                  toggleSignInUp(event);
                },
              },
            ],
            links: [
              {
                id: system.LOGIN_PAGE_CONSTANTS.ID.FORGOT_PASSWORD,
                key: system.LOGIN_PAGE_CONSTANTS.ID.FORGOT_PASSWORD,
                text: Messages.Controls.ForgotPassword,
                href: '#',
                onClick: event => {
                  toggleSignInUp(event);
                  setActivePage(FORGOT_PASSWORD);
                },
              },
            ],
          });
          return [
            <LoginField
              {...{
                key: 'LoginField',
                handleBlur,
                validate: validateLogin,
                isTouched: isTouched[LOGIN_PLACEHOLDER],
                onChange: setLoginValue,
                value: loginValue,
                errorText: loginError,
              }}
            />,
            <PasswordField
              {...{
                key: 'PasswordField',
                handleBlur,
                validate: validatePassword,
                isTouched: isTouched[PASSWORD],
                value: passwordValue,
                errorText: passwordError,
                onChange: setPasswordValue,
              }}
            />,
          ];
      }
    }, [
      activePage,
      loginValue,
      passwordValue,
      emailValue,
      nameValue,
      confirmationValue,
      loginError,
      passwordError,
      isTouched,
    ]);

    const signInRequest = () => {
      if (!loginError && !passwordError) {
        signIn(loginValue, md5(passwordValue)).catch(e => {
          showNotification({msg: e.message, type: SNACKBAR_TYPES.Error});
        });
      }
    };

    const restorePasswordRequest = () => {
      if (emailValue.length) {
        restorePassword(emailValue)
          .then(() => {
            showNotification({msg: Messages.Message.CheckYourEmail, type: SNACKBAR_TYPES.Success});
          })
          .catch(e => {
            showNotification({msg: e.message, type: SNACKBAR_TYPES.Error});
          });
      } else showNotification({msg: Messages.Errors.EmailIsRequired, type: SNACKBAR_TYPES.Error});
    };

    const changePasswordRequest = () => {
      if (passwordValue !== confirmationValue) {
        showNotification({
          msg: Messages.Errors.PassAndConfirmDifferent,
          type: SNACKBAR_TYPES.Error,
        });
        return;
      }
      if (!passwordValue || !confirmationValue) {
        return showNotification({
          msg: `${
            !passwordValue && !confirmationValue
              ? Messages.Errors.PassAndConfirmRequired
              : !passwordValue
              ? Messages.Errors.PassRequired
              : Messages.Errors.ConfirmRequired
          }`,
          type: SNACKBAR_TYPES.Error,
        });
      }

      if (passwordValue.length && passwordValue === confirmationValue && activationCode) {
        changePassword({
          ActivationCode: activationCode,
          Password: md5(passwordValue),
        })
          .then(() => {
            showNotification({msg: Messages.Message.PasswordChanged, type: SNACKBAR_TYPES.Success});
          })
          .then(() => {
            setActivePage(LOGIN);
          })
          .catch(e => {
            showNotification({msg: e.message, type: SNACKBAR_TYPES.Error});
          });
      }
    };

    const validation = requestValues => {
      /** Проверка всех полей на пустоту */
      const searchEmptyValues = requestValues.filter(
        field => !field.fieldValue.length || field.fieldValue.trim() === ''
      );

      if (searchEmptyValues.length) {
        const emptyFieldsNames = searchEmptyValues.map(field => field.fieldName);

        if (searchEmptyValues.length === 1) {
          throw new Error(
            formatMessage(`${Messages.Controls.Value} {0} {1}`, [emptyFieldsNames, Messages.Errors.IsRequired])
          );
        }
        throw new Error(
          formatMessage(`${Messages.Controls.Values} {0} {1}`, [emptyFieldsNames, Messages.Errors.AreRequired])
        );
      }

      /** Проверка всех полей кроме NAME поля на пустоту в середине строки */
      const searchSpaceInTheMiddle = requestValues.filter(
        field => field.fieldName !== NAME && field.fieldValue.includes(' ')
      );

      if (searchSpaceInTheMiddle.length) {
        throw new Error(
          formatMessage(`${Messages.Controls.Value} {0} {1}`, [
            searchSpaceInTheMiddle.map(field => field.fieldName),
            Messages.Errors.SpacesError,
          ])
        );
      }

      /** Проверка корректности LOGIN */
      if (!loginValidate(loginValue)) {
        throw new Error(formatMessage(`${Messages.Errors.ErrorSymbols}`, [LOGIN_PLACEHOLDER]));
      }

      /** Проверка корректности EMAIL */
      if (!emailValidate(emailValue)) {
        throw new Error(formatMessage(`${Messages.Controls.Value} {0} {1}`, [EMAIL, Messages.Errors.IsIncorrect]));
      }
    };

    const clearFields = () => {
      setPasswordValue('');
      setLoginValue('');
      setNameValue('');
      setEmailValue('');
    };

    const signUpRequest = () => {
      try {
        validation(requestValues);

        signUp({
          Login: loginValue,
          Name: nameValue,
          EMail: emailValue,
          Password: md5(passwordValue),
        })
          .then(() => {
            showNotification({
              msg: Messages.Success.CheckYourEmailToCompleteReg,
              type: SNACKBAR_TYPES.Success,
            });
            setActivePage(LOGIN);
            clearFields();
          })
          .catch(e => {
            showNotification({msg: e.message, type: SNACKBAR_TYPES.Error});
          });
      } catch (e) {
        showNotification({
          msg: e.message,
          type: SNACKBAR_TYPES.Error,
        });
      }
    };

    return (
      <Component
        fields={fields}
        activeContainer={activeContainer}
        additionalContainer={additionalContainer}
        rightPanelActiveToggle={rightPanelActiveToggle}
        {...props}
      />
    );
  };
  return SwitchedPage;
};

export default HOCPageSwitcher;
